import { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/common";
import { MessagesSquare, Send, Plus, Hash, User } from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

const SCOPE_LABEL = { all: "Hela organisationen", department: "Avdelning", team: "Team", business_area: "Verksamhetsområde" };

function NewChannel({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", scope_kind: "all", scope_ref: "" });
  const { data: departments } = useApi("/departments");
  const { data: teams } = useApi("/teams");
  const { data: areas } = useApi("/business_areas");
  const refOpts = f.scope_kind === "department" ? departments : f.scope_kind === "team" ? teams : f.scope_kind === "business_area" ? areas : [];
  const create = async () => {
    try {
      await api.post("/chat/channels", { name: f.name, scope_kind: f.scope_kind, scope_ref: f.scope_ref || null });
      toast.success("Kanal skapad"); setOpen(false); setF({ name: "", scope_kind: "all", scope_ref: "" }); onCreated();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="new-channel-btn"><Plus className="mr-1 h-4 w-4" />Kanal</Button></DialogTrigger>
      <DialogContent data-testid="new-channel-dialog">
        <DialogHeader><DialogTitle>Ny kanal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Namn</Label><Input value={f.name} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} data-testid="channel-name" /></div>
          <div><Label>Vilka?</Label>
            <Select value={f.scope_kind} onValueChange={(v) => setF((s) => ({ ...s, scope_kind: v, scope_ref: "" }))}>
              <SelectTrigger data-testid="channel-scope"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SCOPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
          {f.scope_kind !== "all" && (
            <div><Label>Välj</Label>
              <Select value={f.scope_ref} onValueChange={(v) => setF((s) => ({ ...s, scope_ref: v }))}>
                <SelectTrigger data-testid="channel-ref"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(refOpts || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
        </div>
        <DialogFooter><Button onClick={create} disabled={!f.name} data-testid="channel-create">Skapa</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewDirect({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const { data: users } = useApi("/auth/directory");
  const start = async () => {
    try { const r = await api.post("/chat/direct", { user_email: email }); toast.success("Konversation öppnad"); setOpen(false); setEmail(""); onCreated(r.data.id); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="new-direct-btn"><User className="mr-1 h-4 w-4" />Direkt</Button></DialogTrigger>
      <DialogContent data-testid="new-direct-dialog">
        <DialogHeader><DialogTitle>Nytt direktmeddelande</DialogTitle></DialogHeader>
        <Select value={email} onValueChange={setEmail}>
          <SelectTrigger data-testid="direct-user"><SelectValue placeholder="Välj person" /></SelectTrigger>
          <SelectContent>{(users || []).map((u) => <SelectItem key={u.id} value={u.email}>{u.name} · {u.role_label}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter><Button onClick={start} disabled={!email} data-testid="direct-start">Starta</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadChannels = useCallback(async () => {
    try { const r = await api.get("/chat/channels"); setChannels(r.data); return r.data; } catch (e) { return []; }
  }, []);
  const loadMessages = useCallback(async (cid) => {
    if (!cid) return;
    try { const r = await api.get(`/chat/channels/${cid}/messages`); setMessages(r.data); } catch (e) {}
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);
  useEffect(() => {
    const iv = setInterval(() => { loadChannels(); if (active) loadMessages(active); }, 5000);
    return () => clearInterval(iv);
  }, [active, loadChannels, loadMessages]);
  useEffect(() => { if (active) loadMessages(active); }, [active, loadMessages]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const t = text; setText("");
    try { await api.post(`/chat/channels/${active}/messages`, { text: t }); loadMessages(active); loadChannels(); }
    catch (e) { toast.error(apiErr(e)); setText(t); }
  };

  const activeChan = channels.find((c) => c.id === active);

  return (
    <div>
      <PageHeader title="Meddelanden" icon={MessagesSquare} subtitle="Tvåvägs-chatt: kanaler per avdelning, team eller verksamhetsområde – och direktmeddelanden mellan individer." />
      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <Card className="p-3" data-testid="channel-list">
          <div className="mb-2 flex gap-2"><NewChannel onCreated={loadChannels} /><NewDirect onCreated={async (id) => { await loadChannels(); setActive(id); }} /></div>
          {channels.length === 0 ? <EmptyState icon={MessagesSquare} title="Inga konversationer" text="Skapa en kanal eller starta ett direktmeddelande." /> : (
            <div className="space-y-1">
              {channels.map((c) => (
                <button key={c.id} onClick={() => setActive(c.id)} data-testid={`channel-${c.id}`}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${active === c.id ? "bg-secondary" : "hover:bg-secondary/50"}`}>
                  {c.type === "direct" ? <User className="h-4 w-4 shrink-0 text-accent" /> : <Hash className="h-4 w-4 shrink-0 text-accent" />}
                  <span className="flex-1 truncate">
                    <span className="font-medium">{c.name}</span>
                    {c.last_message && <span className="block truncate text-xs text-muted-foreground">{c.last_message}</span>}
                  </span>
                  {c.unread > 0 && <Badge className="bg-red-600">{c.unread}</Badge>}
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="flex h-[70vh] flex-col p-0" data-testid="chat-thread">
          {!activeChan ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Välj en konversation</div>
          ) : (
            <>
              <div className="flex items-center gap-2 border-b px-4 py-3">
                {activeChan.type === "direct" ? <User className="h-4 w-4 text-accent" /> : <Hash className="h-4 w-4 text-accent" />}
                <span className="font-medium">{activeChan.name}</span>
                {activeChan.type === "channel" && activeChan.scope && <Badge variant="outline" className="ml-2">{SCOPE_LABEL[activeChan.scope.kind]}</Badge>}
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-4 kr-scroll">
                {messages.map((m) => {
                  const mine = m.sender_email === user?.email;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                        {!mine && <div className="text-xs font-semibold opacity-80">{m.sender_name}</div>}
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={`mt-0.5 text-[10px] ${mine ? "text-blue-200" : "text-muted-foreground"}`}>{fDateTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="flex items-center gap-2 border-t p-3">
                <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Skriv ett meddelande…" data-testid="chat-input" />
                <Button onClick={send} disabled={!text.trim()} data-testid="chat-send"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
